import { useEffect, useState, useCallback } from "react";
import { Download, KeyRound, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlanStatusBadge } from "@/components/common";
import { DocumentBox } from "@/components/admin/master/DocumentBox";
import { PontoHomologacaoBox } from "@/components/admin/master/PontoHomologacaoBox";
import { FiscalDocumentosLojaBox } from "@/components/admin/master/FiscalDocumentosLojaBox";
import { adminApi, brl, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { downloadBlob } from "@/lib/exporters";
import { toast } from "sonner";

function Info({ label, value, testId }) {
  return <div><p className="eyebrow">{label}</p><p className="font-medium" data-testid={testId}>{value}</p></div>;
}

export function TenantDetailDialog({ tenantId, onOpenChange, onChanged }) {
  const [d, setD] = useState(null);
  const [days, setDays] = useState("");
  const load = useCallback(() => tenantId && adminApi.get(`/tenants/${tenantId}`).then((r) => { setD(r.data); setDays(String(r.data.subscription.trial_days)); }), [tenantId]);
  useEffect(() => { setD(null); load(); }, [load]);
  const saveDays = async () => {
    try {
      await adminApi.patch(`/tenants/${tenantId}`, { trial_days: Number(days) });
      toast.success("Período de teste alterado");
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const resetAccess = async () => {
    if (!window.confirm("Gerar um novo número de acesso? Todas as sessões do cliente serão encerradas.")) return;
    try {
      const { data } = await adminApi.post(`/tenants/${tenantId}/reset-access`);
      toast.success(`Novo número de acesso: ${data.access_number}${data.email_sent ? " (enviado por e-mail)" : ""}`, { duration: 10000 });
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const backup = async () => {
    try {
      const r = await adminApi.get(`/tenants/${tenantId}/backup`, { responseType: "blob" });
      downloadBlob(r.data, `backup_${d.access_number}.json`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!tenantId} onOpenChange={(v) => !v && onOpenChange()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tenant-detail-dialog">
        <DialogHeader><DialogTitle>{d?.nome_fantasia || "Carregando..."}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Info label="Número de acesso" value={<span className="font-mono tracking-widest">{d.access_number}</span>} testId="detail-access-number" />
              <Info label="Status" value={<PlanStatusBadge status={d.subscription.status} testId="detail-status" />} />
              <Info label="Plano" value={d.plan || "—"} testId="detail-plan" />
              <Info label="Pago até" value={fmtDate(d.subscription.paid_until)} />
              <Info label="Usuários" value={d.users} testId="detail-users" />
              <Info label="Vendas" value={d.sales} testId="detail-sales" />
              <Info label="Faturamento" value={brl(d.revenue)} testId="detail-revenue" />
              <Info label="Produtos / unidades" value={`${d.products} / ${d.stores}`} />
              <Info label="Teste" value={`${fmtDate(d.subscription.trial_start)} → ${fmtDate(d.subscription.trial_end)}`} />
              <Info label="Dias restantes" value={d.subscription.days_left} testId="detail-days-left" />
              <Info label="Proprietário" value={d.owner ? `${d.owner.name} (${d.owner.login})` : "—"} />
              <Info label="Último acesso" value={fmtDateTime(d.owner?.last_login_at)} />
            </div>
            <div className="flex flex-wrap items-end gap-2 border-t pt-4">
              <div><p className="eyebrow mb-1">Dias de teste (a partir do cadastro)</p><Input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} className="w-32" data-testid="detail-trial-days-input" /></div>
              <Button variant="outline" onClick={saveDays} data-testid="detail-save-trial-btn"><Save className="h-4 w-4 mr-1" />Salvar dias</Button>
              <Button variant="outline" onClick={resetAccess} data-testid="detail-reset-access-btn"><KeyRound className="h-4 w-4 mr-1" />Redefinir número de acesso</Button>
              <Button variant="outline" onClick={backup} data-testid="detail-backup-btn"><Download className="h-4 w-4 mr-1" />Backup (JSON)</Button>
            </div>
            <DocumentBox tenantId={tenantId} documento={d.documento} historico={d.documento_historico} onChanged={() => { load(); onChanged(); }} />
            {d.fiscal_documentos && <FiscalDocumentosLojaBox tenantId={tenantId} documentos={d.fiscal_documentos} onChanged={load} />}
            {d.ponto_recursos && <PontoHomologacaoBox tenantId={tenantId} recursos={d.ponto_recursos} onChanged={load} />}
            <div className="border-t pt-4">
              <p className="eyebrow mb-2">Últimas ações</p>
              <ul className="divide-y max-h-56 overflow-y-auto" data-testid="detail-logs">
                {d.logs.map((l) => <li key={l.id} className="py-1.5 flex justify-between gap-3"><span>{l.action} <span className="text-muted-foreground">· {l.actor_name}</span>{l.details && <span className="block text-xs text-muted-foreground" data-testid="detail-log-details">{l.details}</span>}</span><span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(l.created_at)}</span></li>)}
                {!d.logs.length && <li className="py-2 text-muted-foreground">Sem registros.</li>}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
